import { catchError, first, map, switchMap } from 'rxjs/operators';
import { select, Store } from '@ngrx/store';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { AppState } from '../_reducers';
import { Commodity } from '../_models/commodity';
import { environment } from '../../../environments/environment';
import { GeneralUtilsService } from './common/generalUtils.service';
import { selectAllCommodities, selectCommodityById } from '../_selectors/commodity.selectors';

@Injectable()
export class CommodityService {
  private apiBaseUrl = environment.apiBaseUrl;
  constructor(
    private store: Store<AppState>,
    private http: HttpClient,
    private generalUtilsService: GeneralUtilsService
  ) {}

  getFromLocalStore(): Observable<Commodity[]> {
    return this.store.pipe(select(selectAllCommodities));
  }

  getCommodities(): Observable<Commodity[]> {
    return this.http
      .get<Commodity[]>(this.apiBaseUrl + 'api/private/v1/lists/commodities')
      .pipe(map((response) => this.removeNoCult(response)));
  }
  getCommodity(id: number): Observable<Commodity> {
    return this.http
      .get<any>(this.apiBaseUrl + 'api/private/v1/lists/commodities/' + id)
      .pipe(map((response) => response));
  }
  saveCommodity(commodity): Observable<Commodity> {
    const companySelected = JSON.parse(localStorage.getItem('companySelected'));
    commodity.companyId = companySelected ? companySelected.id : null;
    if (commodity.id) {
      return this.http
        .put(this.apiBaseUrl + 'api/private/v1/lists/commodities/' + commodity.id, commodity)
        .pipe(map((response) => response));
    } else {
      return this.http
        .post(this.apiBaseUrl + 'api/private/v1/lists/commodities/', commodity)
        .pipe(map((response) => response));
    }
  }
  deleteCommodity(id: number): Observable<Commodity> {
    return this.http.delete(this.apiBaseUrl + 'api/private/v1/lists/commodities/' + id).pipe(
      map(() => {
        return { id } as Commodity;
      })
    );
  }

  getCommodityByID(id: number): Observable<Commodity | { name: string }> {
    return this.store.pipe(
      select(selectCommodityById(id)),
      map((commodity) => (commodity ? commodity : of({ name: 'No Cultivation' }))),
      // Utilizza l'operatore switchMap per gestire il caso di un Observable che ritorna un Observable
      switchMap((result) => (result instanceof Observable ? result : of(result)))
    );
  }

  getLocalCommodityById = (id: number): Commodity => {
    let item: Commodity;
    this.getFromLocalStore()
      .pipe(first((list: Commodity[]) => list && list.length > 0))
      .subscribe((list: Commodity[]) => {
        item = list.find((i) => i.id === id);
      });
    return item;
  };

  getCommodityNameById = (id: number): string => {
    const commodity = this.getLocalCommodityById(id);
    if (commodity && commodity.name) {
      return commodity.name;
    } else {
      return null;
    }
  };

  getCommodityIdByName = (name: string): Commodity => {
    let item: Commodity;
    this.getFromLocalStore()
      .pipe(first((list: Commodity[]) => list && list.length > 0))
      .subscribe((list: Commodity[]) => {
        item = list.find((i) => i.name === name);
      });
    return item;
  };

  getCommodityList = (): Observable<Array<Commodity>> => {
    return this.getFromLocalStore();
  };

  getCommodityColorById = (id: number, fallbackRandom = true) => {
    const commodity: Commodity = this.getLocalCommodityById(id);

    return commodity && commodity.colorR && commodity.colorG && commodity.colorB
      ? 'rgba(' + commodity.colorR + ',' + commodity.colorG + ',' + commodity.colorB + ')'
      : fallbackRandom
        ? this.generalUtilsService.getRandomColor()
        : '#000000';
  };

  private removeNoCult(comms: any) {
    const itemsNoCultivation = comms.filter((it) => it['name'] !== 'No Cultivation');

    if (itemsNoCultivation.length > 0) {
      return itemsNoCultivation;
    } else {
      return comms;
    }
  }

  getCommodityCountryMapping(id: number): Observable<{ status: number; body: any }> {
    return this.http
      .get<{
        status: number;
        body: any;
      }>(`${this.apiBaseUrl}api/private/v1/commodityCountryMapping/${id}`, { observe: 'response' })
      .pipe(
        map((response) => {
          return {
            status: response.status,
            body: response.body, // HttpClient già effettua automaticamente il parsing del JSON
          };
        }),
        catchError((error) => {
          return throwError(() => ({
            status: error.status,
            body: error.error, // Usa 'error.error' per ottenere il corpo della risposta di errore
          }));
        })
      );
  }

  getCommodityCountryFields(
    id: number,
    idCountryCommodity: number = 0
  ): Observable<{ status: number; body: any }> {
    return this.http
      .get<{
        status: number;
        body: any;
      }>(
        `${this.apiBaseUrl}api/private/v1/commodityCountryFields/${id}?idCountryCommodity=${idCountryCommodity}`,
        { observe: 'response' }
      )
      .pipe(
        map((response) => {
          return {
            status: response.status,
            body: response.body,
          };
        }),
        catchError((error) => {
          return throwError(() => ({
            status: error.status,
            body: error.error,
          }));
        })
      );
  }

  getCountryFieldsFromCommodity(
    id: number,
    idCommodity: number = 0
  ): Observable<{ status: number; body: any }> {
    return this.http
      .get<{
        status: number;
        body: any;
      }>(
        `${this.apiBaseUrl}api/private/v1/commodityCountryFields/${id}?idCommodity=${idCommodity}`,
        { observe: 'response' }
      )
      .pipe(
        map((response) => {
          return {
            status: response.status,
            body: response.body,
          };
        }),
        catchError((error) => {
          return throwError(() => ({
            status: error.status,
            body: error.error,
          }));
        })
      );
  }
}
